import React, {useState, useEffect, useCallback } from 'react';
import {useNavigate } from 'react-router-dom';
import {getAuth, User, signOut } from 'firebase/auth';

import {
  AdminSidebar,
  AdminLayout,
  MainContent,
  UserInfo,
  LogoutButton,
  SidebarList,
  SidebarItem,
  SidebarHeader,
  InactivityWarning,
  CountdownTimer,
  Button,
} from './ModeratorDashboard.styles';
import SaleSubmissionsAdmin from './SaleSubmissionsAdmin'; 
import StallManagement from './StallManagement';
import PurchaseRequestsAdmin from './PurchaseRequestsAdmin';
import VerificationManagement from './documentVerification';
import EstateManagement from './RealEstateManagement';
import SuppliersManagement from './SupplierManagement';

interface ModeratorDashboardProps {
  onLogout: () => void;
  isModerator: boolean; 
}

const ModeratorDashboard: React.FC<ModeratorDashboardProps> = ({onLogout, isModerator }) => {
  const [activeSection, setActiveSection] = useState<
    'realEstateManagement' | 
    'saleSubmissions' |
    'supplierManagement'|
    'purchaseRequests' |
    'verificationManagement' |
    'stallmanagement' |
    'contentModeration' 
  >('contentModeration');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60); 
  const [inactivityTimer, setInactivityTimer] = useState<NodeJS.Timeout | null>(null);
  const [countdownTimer, setCountdownTimer] = useState<NodeJS.Timeout | null>(null);
  
  const auth = getAuth();
  const navigate = useNavigate();
 
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (countdownTimer) clearInterval(countdownTimer);
    
    setShowWarning(false);
    setCountdown(60);

    const timer = setTimeout(() => {
      setShowWarning(true);
      
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            handleAutoLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setCountdownTimer(countdownInterval);
    }, 3600000);

    setInactivityTimer(timer);
  }, [inactivityTimer, countdownTimer]);

  // Handle auto logout
  const handleAutoLogout = useCallback(async () => {
    try {
      await signOut(auth);
      onLogout();
      navigate('/moderator/login'); 
    } catch (error) {
      console.error('Error during auto logout:', error);
    }
  }, [auth, onLogout, navigate]);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Set up event listeners for user activity
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity]);

  // Initialize timer on component mount
  useEffect(() => {
    resetInactivityTimer();
    
    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      if (countdownTimer) clearInterval(countdownTimer);
    };
  }, [resetInactivityTimer, inactivityTimer, countdownTimer]);

  // --- Authentication Check ---

  useEffect(() => {
    const user = auth.currentUser;
    setCurrentUser(user);

    if (!isModerator) {
      // Redirect to home or login if not authorized
      navigate('/'); 
    }
  }, [isModerator, navigate, auth]);

  if (!isModerator) return null; // Prevent rendering if not a moderator

  // --- Render ---

  return (
    // Use AdminLayout/AdminSidebar, but conceptually they are now ModeratorLayout/ModeratorSidebar
    <AdminLayout> 
      {/* Inactivity Warning Modal (Keep as is) */}
      {showWarning && (
        <InactivityWarning>
          <div style={{
            backgroundColor: 'white', 
            padding: '2rem', 
            borderRadius: '8px', 
            textAlign: 'center',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            minWidth: '300px'
          }}>
            <h3 style={{color: '#e74c3c', marginBottom: '1rem' }}>Session Timeout Warning</h3>
            <p>You will be logged out due to inactivity in</p>
            <CountdownTimer>{countdown} seconds</CountdownTimer>
            <p>Move your mouse or press any key to continue your session.</p>
            <Button 
              onClick={resetInactivityTimer}
              style={{marginTop: '1rem' }}
            >
              Continue Session
            </Button>
          </div>
        </InactivityWarning>
      )}

      <AdminSidebar>
        <SidebarHeader>
          <h2>Moderator Dashboard 🛡️</h2>
        </SidebarHeader>

        {currentUser && (
          <UserInfo>
            <div style={{fontWeight: 'bold', marginBottom: '4px' }}>
              {currentUser.displayName || currentUser.email || 'Moderator User'}
            </div>
            <div style={{fontSize: '0.8rem', color: '#666' }}>
              {currentUser.email}
            </div>
          </UserInfo>
        )}

        <SidebarList>

          <SidebarItem
            className={activeSection === 'realEstateManagement' ? 'active' : ''}
            onClick={() => setActiveSection('realEstateManagement')}
          >
            RealEstate Management          
          </SidebarItem>

          <SidebarItem
            className={activeSection === 'supplierManagement' ? 'active' : ''}
            onClick={() => setActiveSection('supplierManagement')}
          >
            Supplier Management          
          </SidebarItem>

          <SidebarItem
            className={activeSection === 'stallmanagement' ? 'active' : ''}
            onClick={() => setActiveSection('stallmanagement')}
          >
           Stall Management
          </SidebarItem>                    
          
          <SidebarItem
            className={activeSection === 'saleSubmissions' ? 'active' : ''}
            onClick={() => setActiveSection('saleSubmissions')}
          >
            Sale Submissions
          </SidebarItem>
          
          <SidebarItem
            className={activeSection === 'purchaseRequests' ? 'active' : ''}
            onClick={() => setActiveSection('purchaseRequests')}
          >
            Purchase Requests
          </SidebarItem>
          
          <SidebarItem
            className={activeSection === 'verificationManagement' ? 'active' : ''}
            onClick={() => setActiveSection('verificationManagement')}
          >
             Document Verification
          </SidebarItem>

          {/* Removed Admin-Only Sections:
          User Management, Real Estate Management, Administrative Units, 
          Admin Management, Stall Config & Prices, Supplier Management, 
          System Settings, Reports. 
          Keep other generic items if they apply to a moderator. 
          */}
          
        </SidebarList>

        <LogoutButton onClick={onLogout}>
          Logout
        </LogoutButton>
      </AdminSidebar>

      <MainContent>
        {activeSection === 'realEstateManagement' && <EstateManagement />}
        {activeSection === 'saleSubmissions' && <SaleSubmissionsAdmin />} 
        {activeSection === 'stallmanagement' && <StallManagement />}
        {activeSection === 'purchaseRequests' && <PurchaseRequestsAdmin />}
        {activeSection === 'supplierManagement' && <SuppliersManagement />}        
        {activeSection === 'verificationManagement' && <VerificationManagement />}        

      </MainContent>
    </AdminLayout>
  );
};

export default ModeratorDashboard;